import { useSyncExternalStore } from 'react'
import { APP_VERSIONS, type AppName } from './appVersion'

// Knowing when the running app is older than what is deployed.
//
// The service worker already updates an install by itself, but quietly and on
// its own schedule, and not at all where no service worker runs. The Worker
// stamps every response with the latest release of each app, so the next API
// call tells an old tab the truth, and the app can say so.
//
// Each app has its own number and reads only its own header: a release of the
// gym app never shows members the banner, and the reverse.

/**
 * The response header the Worker reports each app's latest release in. The
 * Worker sets the same names (workers/api/src/index.ts).
 */
export const VERSION_HEADERS: Record<AppName, string> = {
  gym: 'X-Gym-Version',
  member: 'X-Member-Version',
}

const SEMVER = /^(\d+)\.(\d+)\.(\d+)$/

/**
 * True when `server` is a later release than `app`.
 *
 * Later, not merely different: the apps and the Worker deploy as separate jobs,
 * so for a minute a freshly loaded app can be ahead of the Worker. Telling that
 * member to refresh would be wrong, and no refresh would clear it. Anything
 * that is not `x.y.z` counts as not newer.
 */
export function isNewerVersion(server: string | null | undefined, app: string): boolean {
  const a = SEMVER.exec(server ?? '')
  const b = SEMVER.exec(app)
  if (!a || !b) return false
  for (let i = 1; i <= 3; i++) {
    const diff = Number(a[i]) - Number(b[i])
    if (diff !== 0) return diff > 0
  }
  return false
}

let updateAvailable = false
const listeners = new Set<() => void>()

function setUpdateAvailable(next: boolean) {
  if (updateAvailable === next) return
  updateAvailable = next
  listeners.forEach((notify) => notify())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** True once the Worker has reported a later version than this bundle. */
export function useUpdateAvailable(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => updateAvailable,
    () => false,
  )
}

function getUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return (input as Request).url
}

export interface VersionCheckOptions {
  /** Only responses from this origin carry a version worth reading. */
  apiUrl: string
  /** Which app this is: it reads only that app's header. */
  app: AppName
  /** The version of the running bundle. Defaults to the one it was built from. */
  version?: string
  /** Called once, the first time the server turns out to be ahead. */
  onOutdated?: (versions: { app: string; server: string }) => void
}

/**
 * Reads the Worker's version off every API response and raises the flag behind
 * `useUpdateAvailable` when it is ahead of the app. Costs no request of its own.
 * Returns an uninstall function.
 */
export function installVersionCheck(options: VersionCheckOptions): () => void {
  const { apiUrl, app, version = APP_VERSIONS[app], onOutdated } = options
  const header = VERSION_HEADERS[app]
  const previousFetch = window.fetch
  const _fetch = previousFetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const res = await _fetch(input, init)
    if (!updateAvailable && getUrl(input).startsWith(apiUrl)) {
      const server = res.headers.get(header)
      if (server && isNewerVersion(server, version)) {
        setUpdateAvailable(true)
        onOutdated?.({ app: version, server })
      }
    }
    return res
  }

  return () => {
    window.fetch = previousFetch
    setUpdateAvailable(false)
  }
}

/**
 * Loads the latest build, for certain.
 *
 * A plain reload is not enough: the service worker answers it from the precache,
 * which is the very build we are trying to leave. So the worker and its caches
 * go first. The shell is served `no-cache`, so the reload then fetches the
 * current one, and `registerServiceWorker` installs the new worker on that load.
 *
 * Every step is allowed to fail. Whatever happens, the page reloads.
 */
export async function refreshToLatest(): Promise<void> {
  try {
    const registrations = (await navigator.serviceWorker?.getRegistrations()) ?? []
    await Promise.all(registrations.map((registration) => registration.unregister()))
  } catch {
    // No service worker here, or the browser refused. Reload regardless.
  }
  try {
    const keys = await window.caches.keys()
    await Promise.all(keys.map((key) => window.caches.delete(key)))
  } catch {
    // Cache Storage is missing outside secure contexts.
  }
  window.location.reload()
}
